import React, { useState, useEffect, useCallback } from "react";
import style from "./style.module.css";
import { useCookies } from "react-cookie";
import { authenticatedFetch } from "../../utils/api";
import { API_BASE } from "../../config/api";
import { formatCurrency } from "../../config/currency";

interface ReportsSummary {
  total_orders: number;
  total_revenue: number;
  avg_order_value: number;
  total_customers: number;
}

interface StatusCount {
  status_name: string;
  count: number;
}

interface OrderByType {
  order_type: string;
  count: number;
}

interface TopProduct {
  rank: number;
  product_id: number;
  product_name: string;
  units_sold: number;
  revenue: number;
}

type DateRange = "today" | "week" | "month" | "all";

const getDateRange = (range: DateRange): { from?: string; to?: string } => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const fmt = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const today = fmt(now);

  if (range === "today") return { from: today, to: today };
  if (range === "week") {
    const start = new Date(now);
    start.setDate(now.getDate() - 6);
    return { from: fmt(start), to: today };
  }
  if (range === "month") {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: fmt(start), to: today };
  }
  return {};
};

const STATUS_COLORS: Record<string, string> = {
  pending: "#f59e0b",
  processing: "#3b82f6",
  shipped: "#8b5cf6",
  delivered: "#10b981",
  cancelled: "#ef4444",
};

const ReportsPage = () => {
  const [cookies] = useCookies();
  const [dateRange, setDateRange] = useState<DateRange>("month");
  const [isLoading, setIsLoading] = useState(false);
  const [summary, setSummary] = useState<ReportsSummary | null>(null);
  const [statusCounts, setStatusCounts] = useState<StatusCount[]>([]);
  const [ordersByType, setOrdersByType] = useState<OrderByType[]>([]);
  const [topProducts, setTopProducts] = useState<TopProduct[]>([]);

  const fetchReports = useCallback(async () => {
    setIsLoading(true);
    try {
      const { from, to } = getDateRange(dateRange);
      const params = new URLSearchParams();
      if (from) params.set("from", from);
      if (to) params.set("to", to);

      const res = await authenticatedFetch(
        `${API_BASE}/reports/summary?${params.toString()}`,
        { method: "GET" },
        cookies.auth
      );
      if (res.ok) {
        const data = await res.json();
        setSummary(data.summary);
        setStatusCounts(data.order_status_counts || []);
        setOrdersByType(data.orders_by_type || []);
        setTopProducts(data.top_products || []);
      }
    } catch (err) {
      console.error("Error fetching reports:", err);
    } finally {
      setIsLoading(false);
    }
  }, [dateRange, cookies.auth]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const RANGE_LABELS: Record<DateRange, string> = {
    today: "Today",
    week: "This Week",
    month: "This Month",
    all: "All Time",
  };

  return (
    <div className={style.wrapper}>
      <div className={style.header}>
        <h1 className={style.title}>Reports</h1>
        <div className={style.rangeButtons}>
          {(["today", "week", "month", "all"] as DateRange[]).map((r) => (
            <button
              key={r}
              className={`${style.rangeBtn} ${dateRange === r ? style.rangeBtnActive : ""}`}
              onClick={() => setDateRange(r)}
            >
              {RANGE_LABELS[r]}
            </button>
          ))}
          <button className={style.refreshBtn} onClick={fetchReports} disabled={isLoading}>
            {isLoading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      {isLoading && !summary ? (
        <div className={style.loadingState}>Loading reports…</div>
      ) : (
        <>
          <div className={style.summaryCards}>
            <div className={`${style.card} ${style.cardRevenue}`}>
              <div className={style.cardIcon}>₨</div>
              <div className={style.cardBody}>
                <div className={style.cardValue}>{summary ? formatCurrency(summary.total_revenue) : "—"}</div>
                <div className={style.cardLabel}>Total Revenue</div>
              </div>
            </div>
            <div className={`${style.card} ${style.cardOrders}`}>
              <div className={style.cardIcon}>📦</div>
              <div className={style.cardBody}>
                <div className={style.cardValue}>{summary?.total_orders ?? "—"}</div>
                <div className={style.cardLabel}>Total Orders</div>
              </div>
            </div>
            <div className={`${style.card} ${style.cardCustomers}`}>
              <div className={style.cardIcon}>👥</div>
              <div className={style.cardBody}>
                <div className={style.cardValue}>{summary?.total_customers ?? "—"}</div>
                <div className={style.cardLabel}>Total Customers</div>
              </div>
            </div>
            <div className={`${style.card} ${style.cardAvg}`}>
              <div className={style.cardIcon}>≈</div>
              <div className={style.cardBody}>
                <div className={style.cardValue}>{summary ? formatCurrency(summary.avg_order_value) : "—"}</div>
                <div className={style.cardLabel}>Avg Order Value</div>
              </div>
            </div>
          </div>

          <div className={style.bottomGrid}>
            <div className={style.section}>
              <h2 className={style.sectionTitle}>Order Status Breakdown</h2>
              {statusCounts.length === 0 ? (
                <p className={style.emptyText}>No order data.</p>
              ) : (
                <div className={style.statusList}>
                  {statusCounts.map((s) => (
                    <div key={s.status_name} className={style.statusRow}>
                      <span
                        className={style.statusDot}
                        style={{ background: STATUS_COLORS[s.status_name] || "#94a3b8" }}
                      />
                      <span className={style.statusName}>{s.status_name}</span>
                      <span className={style.statusCount}>{s.count}</span>
                    </div>
                  ))}
                  {ordersByType.map((o) => (
                    <div key={o.order_type} className={style.statusRow}>
                      <span className={style.statusDot} style={{ background: o.order_type === "delivery" ? "#6366f1" : "#f97316" }} />
                      <span className={style.statusName}>{o.order_type} orders</span>
                      <span className={style.statusCount}>{o.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={style.section}>
              <h2 className={style.sectionTitle}>Top Products by Units Sold</h2>
              {topProducts.length === 0 ? (
                <p className={style.emptyText}>No sales data.</p>
              ) : (
                <div className={style.topTableWrapper}>
                  <table className={style.topTable}>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Product</th>
                        <th>Units Sold</th>
                        <th>Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topProducts.map((p) => (
                        <tr key={p.product_id}>
                          <td className={style.rank}>
                            <span className={p.rank <= 3 ? style.rankBadgeTop : style.rankBadge}>{p.rank}</span>
                          </td>
                          <td className={style.productCell}>{p.product_name}</td>
                          <td className={style.unitsCell}>{p.units_sold}</td>
                          <td className={style.revenueCell}>{formatCurrency(p.revenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ReportsPage;
